import type { GameConfig } from '../config/gameConfig';
import { DEFAULT_CONFIG } from '../config/gameConfig';
import type { Language } from '../i18n/translations';
import { t } from '../i18n/translations';

export type TutorialDefinition = {
    id: string;
    titleKey: string;
    descriptionKey: string;
    configOverrides: Partial<GameConfig>;
};

export type TutorialListItem = {
    id: string;
    title: string;
    description: string;
    configOverrides: Partial<GameConfig>;
};

export const TUTORIALS: TutorialDefinition[] = [
    {
        id: 'workforce',
        titleKey: 'tutorial.list.workforce.title',
        descriptionKey: 'tutorial.list.workforce.description',
        configOverrides: {
            map: { size: 'small', seed: 2233 },
            tree: { ...DEFAULT_CONFIG.tree, ageInterval: 999999 },
            initial: {
                settlement: {
                    population: 12,
                    tier: 'town',
                    buildings: {
                        houses: 6,
                        stoneHouse: 0,
                        storage: true,
                        lumberjack: 2,
                        sawmill: 1,
                        farm: 1,
                        huntingHut: 1,
                        fishingHut: 0,
                        smokehouse: 0,
                        weaver: 0,
                        blacksmith: 0,
                        barracks: 0,
                        goldMine: 0,
                        mint: 0,
                        caravanPost: 0,
                        stable: 0,
                        quarry: 0,
                        masonry: 0,
                        charcoalPit: 0,
                        ironMine: 0,
                        smelter: 0,
                    },
                },
                inventory: {
                    food: 100,
                    fish: 0,
                    tools: 25,
                    planks: 40,
                    wood: 30,
                    stone: 0,
                    bricks: 0,
                    charcoal: 0,
                    ironOre: 0,
                    ironIngots: 0,
                    fiber: 5,
                    clothRope: 0,
                    leather: 0,
                    weapons: 0,
                    goldOre: 0,
                    coins: 0,
                },
            },
        },
    },
    {
        id: 'weapons',
        titleKey: 'tutorial.list.weapons.title',
        descriptionKey: 'tutorial.list.weapons.description',
        configOverrides: {
            map: { size: 'small', seed: 1101 },
            production: {
                ...DEFAULT_CONFIG.production,
                // build quicker
                builderWorkPerTick: 30,
            },
            initial: {
                settlement: {
                    population: 16,
                    tier: 'town',
                    buildings: {
                        houses: 8,
                        stoneHouse: 0,
                        storage: true,
                        lumberjack: 2,
                        sawmill: 0,
                        farm: 1,
                        huntingHut: 0,
                        fishingHut: 0,
                        smokehouse: 0,
                        weaver: 0,
                        blacksmith: 0,
                        barracks: 0,
                        goldMine: 0,
                        mint: 0,
                        caravanPost: 0,
                        stable: 0,
                        quarry: 0,
                        masonry: 0,
                        charcoalPit: 0,
                        ironMine: 1,
                        smelter: 0,
                    },
                },
                inventory: {
                    food: 1000,
                    fish: 0,
                    tools: 30,
                    planks: 120,
                    wood: 1500,
                    stone: 20,
                    bricks: 40,
                    charcoal: 10,
                    ironOre: 10,
                    ironIngots: 25,
                    fiber: 10,
                    clothRope: 0,
                    leather: 8,
                    weapons: 0,
                    goldOre: 0,
                    coins: 0,
                },
            },
        },
    },
    {
        id: 'starvation',
        titleKey: 'tutorial.list.starvation.title',
        descriptionKey: 'tutorial.list.starvation.description',
        configOverrides: {
            map: { size: 'small', seed: 3344 },
            tree: { ...DEFAULT_CONFIG.tree, ageInterval: 999999 },
            initial: {
                settlement: {
                    population: 10,
                    tier: 'town',
                    buildings: {
                        houses: 5,
                        stoneHouse: 0,
                        storage: true,
                        lumberjack: 0,
                        sawmill: 0,
                        farm: 1,
                        huntingHut: 1,
                        fishingHut: 0,
                        smokehouse: 0,
                        weaver: 0,
                        blacksmith: 0,
                        barracks: 0,
                        goldMine: 0,
                        mint: 0,
                        caravanPost: 0,
                        stable: 0,
                        quarry: 0,
                        masonry: 0,
                        charcoalPit: 0,
                        ironMine: 0,
                        smelter: 0,
                    },
                },
                inventory: {
                    food: 6,
                    fish: 0,
                    tools: 10,
                    planks: 20,
                    wood: 10,
                    stone: 0,
                    bricks: 0,
                    charcoal: 0,
                    ironOre: 0,
                    ironIngots: 0,
                    fiber: 0,
                    clothRope: 0,
                    leather: 0,
                    weapons: 0,
                    goldOre: 0,
                    coins: 0,
                },
            },
        },
    },
    {
        id: 'cold',
        titleKey: 'tutorial.list.cold.title',
        descriptionKey: 'tutorial.list.cold.description',
        configOverrides: {
            map: { size: 'small', seed: 4455 },
            tree: { ...DEFAULT_CONFIG.tree, ageInterval: 999999 },
            initial: {
                settlement: {
                    population: 10,
                    tier: 'town',
                    buildings: {
                        houses: 5,
                        stoneHouse: 0,
                        storage: true,
                        lumberjack: 1,
                        sawmill: 0,
                        farm: 0,
                        huntingHut: 0,
                        fishingHut: 0,
                        smokehouse: 0,
                        weaver: 0,
                        blacksmith: 0,
                        barracks: 0,
                        goldMine: 0,
                        mint: 0,
                        caravanPost: 0,
                        stable: 0,
                        quarry: 0,
                        masonry: 0,
                        charcoalPit: 1,
                        ironMine: 0,
                        smelter: 0,
                    },
                },
                inventory: {
                    food: 40,
                    fish: 0,
                    tools: 10,
                    planks: 10,
                    wood: 20,
                    stone: 0,
                    bricks: 0,
                    charcoal: 0,
                    ironOre: 0,
                    ironIngots: 0,
                    fiber: 0,
                    clothRope: 0,
                    leather: 0,
                    weapons: 0,
                    goldOre: 0,
                    coins: 0,
                },
            },
        },
    },
];

export function getTutorialList(lang: Language): TutorialListItem[] {
    return TUTORIALS.map((tutorial) => ({
        id: tutorial.id,
        title: t(lang, tutorial.titleKey),
        description: t(lang, tutorial.descriptionKey),
        configOverrides: tutorial.configOverrides,
    }));
}
